import React, { createContext, useContext, useRef } from 'react';

import { useAppSelector } from '../../../../../../../app/hooks';
import { AgentInput, Message, ReasoningStep } from '../../../../../../../store/dashboard/askAica/actions';
import { ConversationListItem } from '../../../../../../../store/dashboard/askAica/types';
import { CurrentCompany, AskBackGate, DeclineGate, Candidate } from '../../../../../../../store/globalAskAica/types';

/**
 * The two AskAicaV2 surfaces — a single source of truth for the 'leads'/'portfolio'
 * string values so they're never hand-typed inline (human review #14). The VALUES
 * themselves are a frozen contract (backend `source` field, tests) — never change them,
 * only where they're referenced from.
 */
export const ASK_AICA_SURFACE = {
    LEADS: 'leads',
    PORTFOLIO: 'portfolio',
} as const;

export type AskAicaSurface = (typeof ASK_AICA_SURFACE)[keyof typeof ASK_AICA_SURFACE];

/**
 * The "active Ask AICA data + actions" surface shared by the AskAicaV2 leaf
 * components (MessagesContainer, ReasoningPanel, AskAicaInputBox, LeftPanel,
 * AskAicaV2 itself) — RECUR-27369 Option B (Surface Context).
 *
 * Two producers, both mapping onto this SAME shape:
 *  - LeadsAskAicaSurfaceProvider — thin passthrough of useAskAica() + the leads
 *    askAica slice selectors. Data path stays byte-identical to today.
 *  - PortfolioAskAicaSurfaceProvider — thin passthrough of useGlobalAskAica().
 *
 * Fields prefixed "portfolio-only" are undefined on the leads surface.
 */
export interface AskAicaSurfaceContextValue {
    surface: AskAicaSurface;

    // GlobalMessage (portfolio) is structurally assignable — id/type/content/
    // timestamp match, and every other Message field is optional.
    messages: Message[];
    reasoning?: ReasoningStep;
    isLoading: boolean;
    isLoadingChatHistory: boolean;
    error: string | null;
    hasMoreData: boolean;

    selectedSources: string[];
    setSelectedSources: (sources: string[]) => void;

    sendMessage: (content: string, flowKey?: string, agentInput?: AgentInput) => (() => void) | undefined;
    clearChat: () => void;
    fetchChatHistory: (isInitialFetch?: boolean, conversationId?: string | null, reset?: boolean) => void;
    fetchOrgConversations: () => void;
    /** Leads only — Library file listing has no portfolio equivalent. */
    fetchLibraryFiles?: (orgId: string) => void;
    registerStreamStartedCallback: (callback: (value: boolean) => void) => void;
    messagesEndRef: React.RefObject<HTMLDivElement>;

    /** File-download / add-to-reports context org: leads = route org id,
     * portfolio = the gate-resolved currentCompany.org_id. */
    fileContextOrgId?: string;

    // GlobalConversationListItem (portfolio) has the identical shape.
    conversations: ConversationListItem[];
    isLoadingConversations: boolean;
    /** Leads: the sidebar's selectedConversationId. Portfolio: conversationId
     * (the in-progress/loaded conversation — may be set even before any sidebar
     * selection, e.g. once a live turn returns its first conversation_id). */
    selectedConversationId?: string | null;
    isClearingChat: boolean;
    isViewOnly: boolean;

    /** Leads-only: the org currently driving the chat (org-switch clear guard). */
    activeOrgId?: string | null;
    /** Leads-only: @chuckNorris feedback-mention mode. Always false on portfolio. */
    enableFeedbackMention: boolean;

    /** Both surfaces (Conversations v2) — resets the view and arms the next send
     * to force-create a new conversation (D1/D2); never creates a row itself. */
    newChat?: () => void;
    /** Both surfaces — deletes a specific conversation by id, branching active
     * (resets the view) vs background (leaves it untouched) (D3). */
    deleteConversation?: (conversationId: string) => void;
    /** Both surfaces — true from New Chat click until the next turn's first
     * conversation_id lands (D1/D2). */
    pendingNewConversation?: boolean;

    // ── Portfolio-only gate extras (undefined on leads) ──────────────────────
    currentCompany?: CurrentCompany | null;
    companyLess?: boolean;
    askBack?: AskBackGate | null;
    decline?: DeclineGate | null;
    replyToGate?: (candidate: Candidate) => void;
    dismissAskBack?: () => void;
    clearCompany?: () => void;
    loadConversation?: (conversationId: string) => void;
    /** Fetches the conversation sidebar and resumes the most recent conversation —
     * called once when the surface mounts (mirrors the old useGlobalAskAicaModal's
     * openChat-on-open). Leads has no equivalent (its fetchChatHistory/
     * fetchOrgConversations calls on open already cover this). */
    openChat?: () => void;
}

const AskAicaSurfaceContext = createContext<AskAicaSurfaceContextValue | undefined>(undefined);

export const AskAicaSurfaceProvider = AskAicaSurfaceContext.Provider;

/**
 * Maps the raw leads `askAicaReducer` slice onto the surface context's
 * data-only fields (everything except actions/refs) — the SAME field names
 * and defaults LeadsAskAicaSurfaceProvider derives from its own selectors, so
 * the "leads surface shape" is defined in one place instead of being
 * hand-duplicated in both the real provider and this defensive fallback.
 * Every read is optional-chained: callers may pass a partial/undefined slice
 * (e.g. an isolated test mocking only the fields it cares about).
 */
export const mapLeadsSliceToSurfaceData = (askAica: any) => ({
    surface: ASK_AICA_SURFACE.LEADS,
    messages: askAica?.messages ?? [],
    reasoning: askAica?.reasoning,
    isLoading: askAica?.isLoading ?? false,
    isLoadingChatHistory: askAica?.isLoadingChatHistory ?? false,
    error: askAica?.error ?? null,
    hasMoreData: askAica?.pagination?.hasMoreData ?? false,
    selectedSources: askAica?.uiState?.selectedSources ?? [],
    conversations: askAica?.conversations ?? [],
    isLoadingConversations: askAica?.isLoadingConversations ?? false,
    selectedConversationId: askAica?.selectedConversationId ?? null,
    isClearingChat: askAica?.isClearingChat ?? false,
    activeOrgId: askAica?.activeOrgId ?? null,
    pendingNewConversation: askAica?.pendingNewConversation ?? false,
});

/**
 * Bare-minimum, defensive read of the leads askAica slice — used ONLY when
 * useAskAicaSurface() is called with no provider mounted (isolated component
 * tests that render a shared leaf directly, e.g. MessagesContainer.test.tsx).
 * Production code ALWAYS mounts inside LeadsAskAicaSurfaceProvider or
 * PortfolioAskAicaSurfaceProvider — this fallback never carries real actions.
 */
const useNoProviderFallback = (): AskAicaSurfaceContextValue => {
    const askAica = useAppSelector((state: any) => state?.dashboard?.askAicaReducer);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    return {
        ...mapLeadsSliceToSurfaceData(askAica),
        setSelectedSources: () => {},
        sendMessage: () => undefined,
        clearChat: () => {},
        newChat: () => {},
        deleteConversation: () => {},
        fetchChatHistory: () => {},
        fetchOrgConversations: () => {},
        fetchLibraryFiles: undefined,
        registerStreamStartedCallback: () => {},
        messagesEndRef,
        fileContextOrgId: undefined,
        isViewOnly: false,
        enableFeedbackMention: true,
    };
};

/**
 * Reads the active surface's data + actions. Falls back to a bare leads-slice
 * read (see useNoProviderFallback) when no provider is mounted — this is what
 * keeps every pre-existing leads jest suite green without a wrapper (§4 of the
 * plan). Both hooks are always called (never conditionally) so this stays
 * rules-of-hooks safe; only the RESULT is chosen based on provider presence.
 */
export const useAskAicaSurface = (): AskAicaSurfaceContextValue => {
    const ctx = useContext(AskAicaSurfaceContext);
    const fallback = useNoProviderFallback();
    return ctx ?? fallback;
};

export default AskAicaSurfaceContext;
